// @ts-check
/**
 * **飾り**（Issue #523。ユーザー要望 2026-09-24「縁取りとか至る所で使うような演出は、
 * 部品化して共通化できる？」）。
 *
 * 編集ソフトの言い方なら**レイヤースタイル** —— トランスフォーム（`core/transform`）と同じく
 * **どの部品にも同じ名前で付く**が、違うのは**要る部品だけが名乗る**ところ
 * （盤の紙に縁取りは要らない）。テーマが部品に `decor: ['stroke', 'glow']` と書くと、
 * その組の宣言と既定値が `toTheme` で混ざる。
 *
 * ```
 * 部品
 * ├ トランスフォーム … どれにも付く（core/transform）
 * ├ 飾り            … 名乗った部品に付く（ここ）
 * └ 固有            … テーマが書く
 * ```
 *
 * ## なぜ 1 か所に寄せるのか
 *
 * 同じ「文字の縁取り」が arcade の CSS に 5 か所（名前・1P・デッキ名・LP・手札の枚数）、
 * plain に 2 か所、canvas 側にも 1 か所あって、**太さの変数だけが 7 つ**あった。
 * 「光る縁」も 3 か所で別々に書かれていた。名前が違うだけで中身は同じなので、
 * **語彙をここに、当て方を置き場（DOM / canvas）に**分けた。
 *
 * | 置き場 | 縁取り | にじみ |
 * |---|---|---|
 * | DOM（HUD の文字） | `strokeVars()` ＋ `INK_CLASS`（`canvas/canvas.css` の `.de-ink`） | （まだ使っていない） |
 * | canvas（盤・板） | 太さ `strokeW` の線 | `canvas/paint::glowStroke` の重ね塗り |
 *
 * **DOM 側が CSS 変数なのは `-webkit-text-stroke` がインラインで置けないから**ではなく、
 * `paint-order` と 2 つセットで要るのを 1 か所（`.de-ink`）にまとめるため
 * （ついでに jsdom がこの 2 つを落とすので、インラインだとテストから見えない）。
 *
 * **canvas で `shadowBlur` を使わない**のは、毎フレーム描き直す紙では目に見えて重く、
 * 環境によって出方が違うため（重ね塗りなら同じ絵になる）。
 */

/**
 * @typedef {object} Decor
 * @property {number} strokeW
 * @property {string} strokeColor
 * @property {number} glowW
 * @property {number} glowA
 * @property {number} glowN
 */

/** **飾りの組**（部品が名乗る単位）。組ごとに生えるプロパティ */
export const DECOR_GROUPS = Object.freeze({
  stroke: Object.freeze([
    { key: 'strokeW', label: '縁取りの太さ', min: 0, max: 40, step: 0.5, def: 6, type: 'number' },
    { key: 'strokeColor', label: '縁取りの色', def: '#101010', type: 'color' },
  ]),
  glow: Object.freeze([
    { key: 'glowW', label: 'にじみの広がり', min: 0, max: 80, step: 1, def: 16, type: 'number' },
    { key: 'glowA', label: 'にじみの濃さ', min: 0, max: 1, step: 0.01, def: 0.22, type: 'number' },
    { key: 'glowN', label: 'にじみの重ね', min: 1, max: 12, step: 1, def: 1, type: 'number' },
  ]),
});

/** GUI のフォルダの中の見出し（トランスフォームの次・固有の前に並ぶ） */
export const DECOR_GROUP = '飾り';

/** 素の飾り（全部の組の既定） */
export const DECOR_DEFAULTS = Object.freeze(/** @type {Decor} */ (/** @type {any} */ (
  Object.fromEntries(Object.values(DECOR_GROUPS).flat().map((p) => [p.key, p.def]))
)));

/** 値の鍵 */
export const decorKey = (part, prop) => `dc/${part}/${prop}`;

/** その鍵が飾りのものか（`{ part, prop }` か null） */
export function parseDecorKey(key) {
  const m = /^dc\/(.+)\/([a-zA-Z]+)$/.exec(String(key ?? ''));
  return m ? { part: m[1], prop: m[2] } : null;
}

/** その部品が名乗った組のプロパティ（知らない組の名前は落とす） */
const propsOf = (decor) => (Array.isArray(decor) ? decor : [])
  .filter((g) => DECOR_GROUPS[g])
  .flatMap((g) => DECOR_GROUPS[g]);

/**
 * 部品の一覧 → **宣言**（`core/theme` が `schema` に混ぜる）。
 * **`decor` を名乗っていない部品には何も生やさない**（盤の紙に縁取りのノブを出さない）。
 * @param {ReadonlyArray<{part: string, decor?: readonly string[]}>} parts
 */
export function decorSchema(parts) {
  const out = {};
  (parts ?? []).forEach(({ part, decor }) => {
    propsOf(decor).forEach((p) => {
      out[decorKey(part, p.key)] = {
        type: p.type, label: p.label, min: p.min, max: p.max, step: p.step,
        part, group: DECOR_GROUP,
      };
    });
  });
  return out;
}

/** 部品の一覧 → **既定値**（テーマの `style` が上書きできる） */
export function decorStyle(parts) {
  const out = {};
  (parts ?? []).forEach(({ part, decor }) => {
    propsOf(decor).forEach((p) => { out[decorKey(part, p.key)] = p.def; });
  });
  return out;
}

/** その部品の今の飾り（欠けている値は既定） */
export function decorOf(style, part) {
  const out = { ...DECOR_DEFAULTS };
  Object.keys(DECOR_DEFAULTS).forEach((k) => {
    const v = style?.[decorKey(part, k)];
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  });
  return /** @type {Decor} */ (out);
}

/**
 * **縁取りを当てる要素に付ける class**（実体は `canvas/canvas.css`）。
 * `paint-order: stroke fill` が要るのは、それが無いと縁取りが文字の内側を食って細く見えるため
 * —— 太さと 2 つセットなので、当て方は 1 か所に置いて**テーマは値だけ**を渡す。
 */
export const INK_CLASS = 'de-ink';

/**
 * **文字の縁取り → CSS カスタムプロパティ**（DOM）。太さが 0 なら `null`
 * （`style` に何も足さない ＝ `.de-ink` の既定で縁取り無しになる）。
 *
 * **`-webkit-text-stroke` は継承する**ので、並ぶ文字（1P とデッキ名）は親に 1 回で効く。
 */
export function strokeVars(decor) {
  const w = Number(decor?.strokeW);
  if (!(w > 0)) return null;
  return { '--ink-w': String(w), '--ink-c': decor.strokeColor ?? DECOR_DEFAULTS.strokeColor };
}

/** テーマが使う近道（`strokeVars(decorOf(style, part))`） */
export const strokeCss = (style, part) => strokeVars(decorOf(style, part));
